<template>
  <div class="min-h-screen bg-gradient-to-br from-navy-50 via-white to-primary-50">
    <!-- Hero section -->
    <div class="relative overflow-hidden">
      <!-- Background pattern -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-navy-800"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      
      <!-- Decorative elements -->
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="absolute top-20 left-10 w-32 h-32 bg-gold-400/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div class="text-center animate-fade-in">
          <h1 class="hero-title text-white mb-6">
            <span class="bg-gradient-to-r from-gold-400 to-gold-300 bg-clip-text text-transparent">Actualités</span>
          </h1>
          <p class="hero-subtitle text-white/90 max-w-3xl mx-auto">
            Suivez-nous sur nos réseaux sociaux pour ne rien manquer !
          </p>
        </div>
      </div>
    </div>
    
    <!-- Social media feeds -->
    <div class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Tab navigation -->
        <div class="flex justify-center mb-12">
          <div class="bg-white rounded-lg shadow-lg p-1">
            <button 
              @click="activeTab = 'facebook'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'facebook' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-700 hover:text-blue-600'
              ]"
            >
              Facebook
            </button>
            <button 
              @click="activeTab = 'instagram'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'instagram' 
                  ? 'bg-pink-600 text-white' 
                  : 'text-gray-700 hover:text-pink-600'
              ]"
            >
              Instagram
            </button>
            <button 
              @click="activeTab = 'spotify'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'spotify' 
                  ? 'bg-green-600 text-white' 
                  : 'text-gray-700 hover:text-green-600'
              ]"
            >
              Spotify
            </button>
          </div>
        </div>
        
        <!-- Facebook feed -->
        <div v-if="activeTab === 'facebook'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Facebook</h2>
            <p class="text-gray-600">
              Suivez nos dernières actualités et événements
            </p>
          </div>
          
          <!-- Facebook Page Plugin -->
          <div class="flex justify-center">
            <div class="w-full max-w-lg mx-auto">
              <div class="bg-white rounded-lg shadow-lg p-4">
                <div class="flex justify-center">
                  <iframe 
                    src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Femulationstavelot&tabs=timeline%2Cevents&width=400&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                    width="400" 
                    height="600" 
                    style="border:none;overflow:hidden" 
                    scrolling="no" 
                    frameborder="0" 
                    allowfullscreen="true" 
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    class="rounded-lg shadow-md"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Instagram feed -->
        <div v-if="activeTab === 'instagram'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Instagram</h2>
            <p class="text-gray-600">
              Découvrez nos photos et moments musicaux
            </p>
          </div>
          
          <div class="max-w-4xl mx-auto">
            <div class="bg-white rounded-lg shadow-lg p-6">
              <div class="text-center mb-6">
                <div class="flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-pink-600 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281H7.83c-.49 0-.928.438-.928.928v7.83c0 .49.438.928.928.928h8.449c.49 0 .928-.438.928-.928V8.635c0-.49-.438-.928-.928-.928z"/>
                  </svg>
                  <h3 class="text-2xl font-bold text-gray-900">@emulation_stavelot</h3>
                </div>
                <p class="mt-4 text-gray-600">
                  @emulation_stavelot
                </p>
              </div>
              
              <!-- Instagram Widget -->
              <div class="flex justify-center">
                <div class="instagram-embed-container">
                  <iframe 
                    src="https://www.instagram.com/emulation_stavelot/embed/" 
                    width="100%" 
                    height="480" 
                    frameborder="0" 
                    scrolling="no" 
                    allowtransparency="true"
                    class="rounded-lg shadow-md w-full"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Spotify feed -->
        <div v-if="activeTab === 'spotify'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Spotify</h2>
            <p class="text-gray-600">
              Écoutez notre musique et nos enregistrements
            </p>
          </div>
          
          <div class="max-w-4xl mx-auto">
            <div class="bg-white rounded-lg shadow-lg p-6">
              <div class="text-center mb-6">
                <div class="flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  <h3 class="text-2xl font-bold text-gray-900">Royale Harmonie l'Émulation</h3>
                </div>
                <p class="mt-4 text-gray-600">
                  Notre profil Spotify officiel
                </p>
              </div>
              
              <!-- Spotify Widget -->
              <div class="flex justify-center">
                <iframe 
                  src="https://open.spotify.com/embed/artist/3goQVcNG0KIOQS6nEhh518?utm_source=generator&theme=0" 
                  width="100%" 
                  height="352" 
                  frameborder="0" 
                  allowtransparency="true" 
                  allow="encrypted-media"
                  class="rounded-lg shadow-md max-w-2xl"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'News',
  data() {
    return {
      activeTab: 'facebook'
    }
  }
}
</script>

<style scoped>
/* Custom styles for social media embeds */


/* Instagram embed styling */
.instagram-embed-container {
  width: 100%;
  max-width: 600px; /* Largeur maximale pour les grands écrans */
}

.instagram-embed-container iframe {
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
}

/* Spotify embed styling */
iframe[src*="spotify"] {
  border-radius: 12px;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .instagram-embed-container {
    max-width: 500px;
  }
}

@media (max-width: 768px) {
  .instagram-embed-container {
    max-width: 100%;
  }
  
  .instagram-embed-container iframe {
    width: 100%;
    height: 400px;
  }
}

@media (max-width: 480px) {
  .instagram-embed-container iframe {
    height: 350px;
  }
}

/* Large screens */
@media (min-width: 1200px) {
  .instagram-embed-container {
    max-width: 700px;
  }
}
</style>
